import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { clientViewSessionId, clearClientView, adminViewReturnPath } from '../utils/clientViewAs';
import Loading from './Loading';
import './ClientViewAs.css';

export default function ClientViewAsBoundary({ children }) {
  const { user, loading, viewAs, viewAsError } = useAuth();
  const location = useLocation();
  const [expired, setExpired] = useState(false);
  const [ending, setEnding] = useState(false);
  const [endError, setEndError] = useState('');
  useEffect(() => {
    if (!viewAs) return undefined;
    const check = () => setExpired(Date.now() >= new Date(viewAs.expiresAt).getTime());
    check();
    const timer = setInterval(check, 1000);
    return () => clearInterval(timer);
  }, [viewAs]);
  const endView = async () => {
    setEnding(true); setEndError('');
    try {
      // The cookie is shared between tabs. Resolve its current session before ending it.
      const current = await api.get('/admin/view-as/session', { withCredentials: true, financeViewAs: true });
      await api.delete(`/admin/view-as/sessions/${current.data.data.sessionId}`, { withCredentials: true, financeViewAs: true });
    } catch (error) {
      if (![401, 404].includes(error.response?.status)) {
        setEndError('The view could not end. Please try again.'); setEnding(false); return;
      }
    }
    const returnTo = adminViewReturnPath(sessionStorage.getItem('clientViewAsReturnTo'));
    sessionStorage.removeItem('clientViewAsReturnTo');
    clearClientView();
    // A full navigation discards every client page and its cached data.
    window.location.assign(returnTo);
  };
  const active = Boolean(viewAs || viewAsError || clientViewSessionId());
  if (loading) return <Loading page text="Opening client view..." />;
  const blocked = Boolean(viewAsError || expired || (active && !viewAs));
  const personal = active && /^(\/profile|\/orders|\/payment-plans|\/favourites|\/checkout|\/scanner)(?:\/|$)/.test(location.pathname);
  return <>
    {active ? <aside className="client-view-banner" aria-label="Read-only client view">
      <div><strong>{viewAs?.label || 'Client view'}</strong> · Read-only
        {viewAs && <span> · Ends {new Date(viewAs.expiresAt).toLocaleTimeString('en-GB', { timeZone: 'Europe/London', hour: '2-digit', minute: '2-digit' })} UK time</span>}
        <div>You can explore pages and forms. Changes cannot be saved.</div>
      </div>
      <button type="button" onClick={endView} disabled={ending}>{ending ? 'Ending view…' : 'End view'}</button>
    </aside> : user?.role === 'admin' && <div className="client-view-launch"><Link className="btn btn-secondary btn-sm" to={`/admin/view-as?returnTo=${encodeURIComponent(location.pathname)}`}>View as client</Link></div>}
    {endError && <p className="container" role="alert">{endError}</p>}
    {blocked ? <div className="container client-view-message" role="alert"><h1>Client view paused</h1><p>{viewAsError || 'This client view has expired. End the view to return to your admin account.'}</p></div> :
      personal ? <div className="container client-view-message"><h1>This page is outside the client view</h1><p>Personal tickets, payments and account security are not available in this support view.</p><Link to="/admin">Go to the client dashboard</Link></div> : children}
  </>;
}
